import {
  BarsFilter,
  Xmark,
  ChevronRight,
} from '@unified-checkout/design-system/icons/index';
import { PurchaseList } from './PurchaseList';
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Modal,
  DateRangePicker as DSDateRangePicker,
  Badge,
} from '@unified-checkout/design-system';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { DeviceContext } from 'apps/customer/src/context/device';
import { IColumnsMap } from 'libs/design-system/src/types/data_table';
import { instance } from 'apps/customer/src/services/config';
import { getCardLogo } from '@unified-checkout/kit-components';
import { Transaction } from 'apps/customer/src/types';
import { RefundModal } from 'apps/customer/src/components';
import { RefundTable } from 'apps/customer/src/components/inner_layout/refundModal/RefundTable';
import { DateRangePicker } from 'apps/customer/src/components';
import MerchantCard from 'apps/customer/src/components/inner_layout/purchaseHistory/MerchantCard';

export const PurchaseHistory = () => {
  const { isMobile } = useContext(DeviceContext);
  const navigator = useNavigate();
  const searchParams = new URLSearchParams(useLocation().search);

  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const isSuccessful = searchParams.get('is_successful');
  const isRefunded = searchParams.get('is_refunded');

  const safeFormatDate = (dateString: string | null) => {
    if (!dateString) return null;

    const date = parseISO(dateString);
    return isValid(date) ? format(date, 'd MMM') : format(new Date(), 'd MMM');
  };

  const [page, setPage] = useState(1);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [modalData, setModalData] = useState<Transaction>();
  const [currentDate, setCurrentDate] = useState<[Date, Date] | undefined>();

  const navigateHandler = (pathname: string) => {
    const params = new URLSearchParams(searchParams);
    const newParams = new URLSearchParams(pathname);

    newParams.forEach((value, key) => {
      if (value === '') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    navigator(`?${params.toString()}`);
    setPage(1);
    setIsFilterModalOpen(false);
  };

  const filterModalCloseHandler = () => setIsFilterModalOpen(false);
  const rowClickHandler = (row: Transaction) => {
    row.is_refunded && setModalData(row);
  };
  
  const handleClearDateRange = () => {
    searchParams.delete('from');
    searchParams.delete('to');
    setCurrentDate(undefined);
    navigateHandler(`?${searchParams.toString()}`);
    return;
  };
  
  useEffect(() => {
    if (from && to) setCurrentDate([parseISO(from), parseISO(to)]);
  }, [isSuccessful, isRefunded, from, to]);
  
  const queryFunctionParams = useMemo(() => {
    return {
      ...(isRefunded && { is_refunded: isRefunded }),
      ...(from && { from }),
      ...(to && { to }),
    };
  }, [page, isSuccessful, isRefunded, from, to]);

  return (
    <section className="relative mb-6">
      {isMobile ? (
        <>
          <div className='mb-4'>
            <div className="flex items-center justify-between !mb-6 ">
              <h2 className="!mb-0  page-title">Purchase history</h2>
              <div className=" flex gap-2 end-0 -top-14 [&_path]:!fill-gray-500">
                <div className="flex items-center justify-center h-10 border border-gray-300 rounded-lg w-11">
                  <button onClick={() => setIsFilterModalOpen(true)}>
                    {BarsFilter}
                  </button>
                  <Modal
                    open={isFilterModalOpen}
                    closeOnClickOutside
                    onCloseHandler={filterModalCloseHandler}
                    hasCloseIcon
                    btnsProps={[
                      {
                        color: !isRefunded ? 'primary' : 'secondary',
                        text: 'All Transactions',
                        variant: !isRefunded ? 'contained' : 'outlined',
                        onClickHandler: () => navigateHandler('?is_refunded'),
                      },
                      {
                        color: isRefunded === 'false' ? 'primary' : 'secondary',
                        text: 'Sales Transactions',
                        variant:
                          isRefunded === 'false' ? 'contained' : 'outlined',
                        onClickHandler: () =>
                          navigateHandler('?is_refunded=false'),
                      },
                      {
                        color: isRefunded === 'true' ? 'primary' : 'secondary',
                        text: 'Refunded Transactions',
                        variant:
                          isRefunded === 'true' ? 'contained' : 'outlined',
                        onClickHandler: () =>
                          navigateHandler('?is_refunded=true'),
                      },
                    ]}
                    isMobile={isMobile}
                    title="Show results"
                  />
                </div>
                <div className="[&_button]:!w-full">
                  <DSDateRangePicker
                    iconOnlyTrigger
                    isMobile={isMobile}
                    onApply={(range) =>
                      range &&
                      navigateHandler(
                        `?from=${format(
                          range[0],
                          "yyyy-MM-dd'T'HH:mm:ss"
                        )}&to=${format(range[1], "yyyy-MM-dd'T'HH:mm:ss")}`
                      )
                    }
                    hasBackdrop={isMobile}
                    horizontalPlacement="auto"
                    onClear={handleClearDateRange}
                  />
                </div>
              </div>
            </div>
            {from && to && (
              <div className="border flex items-center gap-1 border-gray-300 rounded-[4px] px-2 w-fit english-body-md-regular text-gray-700">
                <p>
                  {safeFormatDate(from)} to {safeFormatDate(to)}
                </p>
                <button
                  className="size-5 [&_path]:fill-gray-400"
                  onClick={handleClearDateRange}
                >
                  {Xmark}
                </button>
              </div>
            )}
          </div>
          <PurchaseList page={page} />
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-6">
            <DateRangePicker
              value={currentDate}
              onApply={(range: any) =>
                range &&
                navigateHandler(
                  `?from=${format(
                    range[0],
                    "yyyy-MM-dd'T'HH:mm:ss"
                  )}&to=${format(range[1], "yyyy-MM-dd'T'HH:mm:ss")}`
                )
              }
              onClear={handleClearDateRange}
              resetable
              verticalPlacement="auto"
              horizontalPlacement="auto"
              placeholder="Select date range"
            />
            <Dropdown width="fit-content">
              <DropdownButton
                text={`${
                  isRefunded === 'false'
                    ? 'Sales transactions'
                    : isRefunded
                    ? 'Refunded transactions'
                    : 'All transactions'
                }`}
                label="Show"
              />

              <DropdownMenu
                horizontalPlacement="start"
                verticalPlacement="auto"
                width="fit-content"
              >
                <DropdownMenuItem
                  text="All transactions"
                  onClickHandler={() => navigateHandler('?is_refunded')}
                />
                <DropdownMenuItem
                  text="Sales transactions"
                  onClickHandler={() => navigateHandler('?is_refunded=false')}
                />
                <DropdownMenuItem
                  text="Refunded transactions"
                  onClickHandler={() => navigateHandler('?is_refunded=true')}
                />
              </DropdownMenu>
            </Dropdown>
          </div>
          {modalData && (
            <RefundModal
              hasCloseIcon
              open={modalData !== undefined}
              onCloseHandler={() => setModalData(undefined)}
              title={'Refund details'}
              supportingText={`Txn. ID ${modalData.transaction_id}`}
              width="50%"
            >
              <RefundTable
                refunds={modalData.refunds}
                total_refund={modalData.total_refund}
              />
            </RefundModal>
          )}

          <DataTable
            lang="en"
            queries={{
              queryFunction: (filter: any) => {
                const { params, pageSize, currentPage } = filter;
                return instance.get(
                  `api/auth/customer_portal/transactions?page=${
                    currentPage || 1
                  }` +
                    `${params.from ? `&from=${params.from}` : ''}` +
                    `${params.to ? `&to=${params.to}` : ''}` +
                    `${pageSize ? `&page_size=${pageSize}` : ''}` +
                    `${
                      params.is_refunded !== undefined
                        ? `&is_refunded=${params.is_refunded}`
                        : ''
                    }&is_successful=true`
                );
              },
              queryKeys: ['list-transactions'],
              queryFunctionParams: queryFunctionParams,
            }}
            tableBody={{
              columnsMap,
              dataLocation: 'data.results',
              tableId: 'purchase-history',
              rowClickHandler: rowClickHandler,
            }}
            emptyState={{
              text: `No ${
                isRefunded === 'true' ? 'refund' : 'purchase'
              } history`,
            }}
            pagination={{
              dataLocation: {
                totalItems: 'data.count',
              },
              type: 'page_based',
              maxVisiblePages: 6,
            }}
          />
        </>
      )}
    </section>
  );
};

const columnsMap: IColumnsMap = [
  {
    id: 'amount',
    name: 'Amount',
    type: 'custom',
    key: 'amount',
    render: (value, row) => (
      <div className="flex items-center gap-2">
        <span className='text-sm font-medium'>{value}</span>
        {(row.is_refunded || row?.is_voided) && (
          <Badge
            size="sm"
            color="gray"
            trailingIcon={!row?.is_voided ? ChevronRight as ReactElement<'svg'> : undefined}
            text="Refunded"
          />
        )}
      </div>
    ),
  },
  {
    id: 'Date',
    name: 'Date & Time',
    type: 'text',
    key: 'created_at',
  },
  {
    id: 'payment',
    name: 'Payment method',
    type: 'custom',
    key: 'sub_type',
    render: (value) => (
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center size-8 rounded-full bg-gray-100 shrink-0 [&_svg]:size-4">
          {value ? getCardLogo(value) : null}
        </div>
        <span className="text-gray-900 english-body-sm-medium">{value}</span>
      </div>
    ),
  },
  {
    id: 'id',
    name: 'Txn. ID',
    type: 'text',
    key: 'transaction_id',
  },
  {
    id: 'merchant',
    name: 'Retailer',
    type: 'custom',
    key: 'merchant',
    render: (_, fullData) => <MerchantCard name={fullData?.merchant} image={fullData?.logo} />,
  },
];
